/**
 * Citadel auth glue — wires Auth0 access tokens into the API client.
 *
 * Distinct Auth0 OAuth client + audience from platform-ui so the
 * operational-independence boundary is enforced at the identity layer.
 * Even if app.rodiun.io's client is compromised, Citadel's client is not.
 *
 * Sprint: SPR-26.O | BL-29-1790
 * BL-29-2163: tenant-id resolution + API-ready signal so /v1/citadel/*
 * calls carry the X-Tenant-ID header and never race the async token fetch.
 */
import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setAccessToken, setTenantId, isApiReady } from './api';

/**
 * Decode the payload of a JWT without verifying the signature.
 *
 * Verification is the backend's responsibility — the frontend only needs
 * the claims for header injection (tenant_id). Ported from
 * `platform-ui/src/components/auth/useAuthSync.ts` (`decodeJwtPayload`),
 * which is the canonical implementation citadel-ui never carried.
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
    try {
        const base64 = token.split('.')[1];
        if (!base64) return {};
        const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json) as Record<string, unknown>;
    } catch {
        return {};
    }
}

/**
 * useAccessTokenSync — keeps the API client's bearer token + tenant id in
 * step with Auth0 session state. Mount once near the route shell (each
 * page calls it; the hook is idempotent against the module-level caches).
 *
 * Returns `ready` — true once BOTH the bearer token AND tenant id are
 * resolved. Pages gate their initial data load on this so the first
 * /v1/citadel/* request never races the async Auth0 token fetch
 * (BL-29-2163 — the 401 race).
 */
export function useAccessTokenSync(): { ready: boolean } {
    const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
    const [ready, setReady] = useState<boolean>(() => isApiReady());

    useEffect(() => {
        if (!isAuthenticated) {
            setAccessToken(null);
            setTenantId(null);
            setReady(false);
            return;
        }
        let cancelled = false;
        getAccessTokenSilently()
            .then((token) => {
                if (cancelled) return;
                setAccessToken(token);

                // Extract tenant id from the Auth0 JWT custom claim.
                // The Auth0 Action "Set Rodiun Custom Claims" (BL-29-416)
                // stamps the NAMESPACED `https://rodiun.io/tenant_id`
                // claim. operon's InjectTenantContext JWT fallback reads
                // the BARE `tenant_id` claim, so without sending an
                // explicit X-Tenant-ID header (see api.ts) operon would
                // resolve an empty tenant context and 400. Precedence
                // mirrors platform-ui exactly: namespaced claim → bare
                // claim → user.sub (Phase 1 has a 1:1 user:tenant map).
                const claims = decodeJwtPayload(token);
                const tenantId =
                    (claims['https://rodiun.io/tenant_id'] as string) ??
                    (claims['tenant_id'] as string) ??
                    (user?.sub ?? null);
                setTenantId(tenantId);

                setReady(isApiReady());
            })
            .catch(() => {
                if (cancelled) return;
                // Token fetch failed — clear both caches to prevent a
                // stale-token / stale-tenant request, and flip ready off.
                setAccessToken(null);
                setTenantId(null);
                setReady(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, user?.sub, getAccessTokenSilently]);

    return { ready };
}
